import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Trash2, GripHorizontal, Plus, Lock } from 'lucide-react';
import { FormInput, Checkbox, Button, Tooltip } from '@getro/rombo';
import { FieldTypeIcon } from '../components/fieldTypeIcon';
import { useFormFieldDragDrop } from '../hooks/useFormFieldDragDrop';

export const FieldItem = ({ field, index, onUpdate, onDelete, onReorder, autoFocusLabel, onLabelFocused }) => {
  const { errors, touched, setFieldTouched } = useFormikContext();

  const fieldName = `fields[${index}].label`;

  const { fieldRef, dragHandleRef, isDragging, isOver } = useFormFieldDragDrop({
    fieldId: field.id,
    index,
    onReorder,
  });
  const handleOptionDelete = (optionId) => {
    const updatedOptions = field.options.filter((opt) => opt.id !== optionId);
    onUpdate({ options: updatedOptions });
  };

  const handleOptionUpdate = (optionId, newLabel) => {
    const updatedOptions = field.options.map(
      (opt) => (opt.id === optionId ? { ...opt, label: newLabel, value: newLabel } : opt),
      // eslint-disable-next-line function-paren-newline
    );
    onUpdate({ options: updatedOptions });
  };

  const handleAddOption = () => {
    const newOption = {
      id: `opt-${Date.now()}`,
      value: '',
      label: '',
    };
    onUpdate({ options: [...(field.options || []), newOption] });
  };

  return (
    <Box
      ref={fieldRef}
      sx={{
        bg: 'white',
        border: '1px solid',
        borderColor: isOver ? 'primary' : 'border.subtle',
        borderRadius: '4px',
        p: '16px',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s',
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            <FieldTypeIcon type={field.type} showLabel />
          </Flex>
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            {field.systemLocked ? (
              <Tooltip
                position="top"
                content={<Text fontSize="12px">This field is required by the system and cannot be removed</Text>}
                trigger={<Box as={Lock} width="16px" height="16px" sx={{ color: 'text.subtle', cursor: 'help' }} />}
              />
            ) : (
              <Box
                as={Trash2}
                width="16px"
                height="16px"
                onClick={onDelete}
                sx={{
                  cursor: 'pointer',
                  color: 'text.subtle',
                  '&:hover': { color: 'red.500' },
                }}
              />
            )}
            <Box
              ref={dragHandleRef}
              as={GripHorizontal}
              width="16px"
              height="16px"
              sx={{
                cursor: 'grab',
                color: 'text.subtle',
                '&:active': { cursor: 'grabbing' },
              }}
            />
          </Flex>
        </Flex>

        <Box>
          <FormInput
            label="Label"
            placeholder="Enter field label"
            autoFocus={autoFocusLabel}
            onFocus={() => onLabelFocused?.()}
            disabled={field.systemLocked}
            field={{
              name: fieldName,
              value: field.label,
              onChange: (e) => onUpdate({ label: e.target.value }),
              onBlur: () => setFieldTouched(fieldName, true),
            }}
            form={{
              errors: { [fieldName]: errors?.fields?.[index]?.label },
              touched: { [fieldName]: touched?.fields?.[index]?.label },
            }}
          />
        </Box>

        {field.type === 'select' && (
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Box>
              <Text
                sx={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'text.dark',
                  mb: '4px',
                }}
              >
                Options
              </Text>
              <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                {field.options &&
                  field.options.map((option, optionIndex) => (
                    <Flex key={option.id} sx={{ alignItems: 'center', gap: '8px' }}>
                      <Box sx={{ flex: 1, maxWidth: 'calc(100% - 24px)' }}>
                        <FormInput
                          value={option.label}
                          onChange={(e) => handleOptionUpdate(option.id, e.target.value)}
                          placeholder="Option label"
                        />
                      </Box>
                      {optionIndex > 0 && (
                        <Box
                          as={Trash2}
                          width="16px"
                          height="16px"
                          onClick={() => handleOptionDelete(option.id)}
                          sx={{
                            cursor: 'pointer',
                            color: 'text.subtle',
                            flexShrink: 0,
                            '&:hover': { color: 'red.500' },
                          }}
                        />
                      )}
                    </Flex>
                  ))}
                <Box
                  as={Button}
                  type="button"
                  onClick={handleAddOption}
                  variant="secondary"
                  icon={Plus}
                  maxWidth="max-content"
                  size="small"
                >
                  Add option
                </Box>
              </Flex>
            </Box>
          </Flex>
        )}

        {field.type === 'checkbox' && field.allowMultiple && (
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Box>
              <Text
                sx={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'text.dark',
                  mb: '4px',
                }}
              >
                Options
              </Text>
              <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                {field.options &&
                  field.options.map((option, optionIndex) => (
                    <Flex key={option.id} sx={{ alignItems: 'center', gap: '8px' }}>
                      <Box sx={{ flex: 1, maxWidth: 'calc(100% - 24px)' }}>
                        <FormInput
                          value={option.label}
                          onChange={(e) => handleOptionUpdate(option.id, e.target.value)}
                          placeholder="Option label"
                        />
                      </Box>
                      {optionIndex > 0 && (
                        <Box
                          as={Trash2}
                          width="16px"
                          height="16px"
                          onClick={() => handleOptionDelete(option.id)}
                          sx={{
                            cursor: 'pointer',
                            color: 'text.subtle',
                            flexShrink: 0,
                            '&:hover': { color: 'red.500' },
                          }}
                        />
                      )}
                    </Flex>
                  ))}
                <Box
                  as={Button}
                  type="button"
                  onClick={handleAddOption}
                  variant="secondary"
                  icon={Plus}
                  maxWidth="max-content"
                  size="small"
                >
                  Add option
                </Box>
              </Flex>
            </Box>
          </Flex>
        )}

        {field.type === 'checkbox' && (
          <Checkbox
            label="Allow multiple selections"
            checked={field.allowMultiple || false}
            onChange={(e) => onUpdate({ allowMultiple: e.target.checked })}
            labelProps={{ fontWeight: '500' }}
          />
        )}
        <Checkbox
          label="Required"
          checked={field.required}
          onChange={(e) => onUpdate({ required: e.target.checked })}
          disabled={field.systemLocked}
          labelProps={{ fontWeight: '500' }}
        />
      </Flex>
    </Box>
  );
};

FieldItem.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    systemLocked: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    allowMultiple: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  autoFocusLabel: PropTypes.bool,
  onLabelFocused: PropTypes.func,
};

FieldItem.defaultProps = {
  autoFocusLabel: false,
  onLabelFocused: undefined,
};
